import { CSSProperties, FC, useEffect, useMemo, useRef, useState } from 'react';
import './TopSection.css';

import CottageOutlinedIcon from '@mui/icons-material/CottageOutlined';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import KitchenIcon from '@mui/icons-material/Kitchen';
import InventoryIcon from '@mui/icons-material/Inventory';
import TableChartIcon from '@mui/icons-material/TableChart';
import Logout from '@mui/icons-material/Logout';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import WestOutlinedIcon from '@mui/icons-material/WestOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import PrintIcon from '@mui/icons-material/Print';
import HotelIcon from '@mui/icons-material/Hotel';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import useGetUser from '../../../main/hooks/useGetUser';
import { useTranslation } from 'react-i18next';
import IUserMenu from '../../interfaces/IUserMenu';
import { useDispatch } from 'react-redux';
import onLogout from '../../store/stores/user/login.store.on-logout';
import useGetMenu from '../../hooks/useGetMenu';
import ReactCountryFlag from 'react-country-flag';
import NotificationIcon from '../../../pages/mainLanding/Landing/NotificationIcon';
import { ApiConstants } from '../../constants/ApiConstants';
import { buildEntityFileUrl } from '../../utils/fileUrl';
import { THEMES, useAppTheme } from '../../theme/AppThemeContext';

type DrawerStage = 0 | 1 | 2;
type DockPlacement = 'left' | 'top';

interface Props {
  stage: DrawerStage;
  onToggleMenu: () => void;
  dockPlacement: DockPlacement;
  onSetDockPlacement: (placement: DockPlacement) => void;
  onNavigate?: () => void;
}

const SINGLE_COLUMN_WIDTH = 280;
const TWO_COLUMN_WIDTH = 500;
const THREE_COLUMN_WIDTH = 690;

const normalizePath = (p?: string) => `/${(p ?? '').trim().replace(/^\/+/, '')}`.replace(/\/+/g, '/');
const menuRouteSource = (item?: Partial<IUserMenu['item']> | any) => item?.url || item?.code || '';

const menuUrlToUiRoute = (url?: string) => {
  const raw = (url ?? '').trim().toLowerCase();
  if (!raw) return '/';

  if (raw === 'dashboard') return '/dashboard-restaurant';
  if (raw === 'dashboard-restaurant') return '/dashboard-restaurant';
  if (raw === 'restaurant') return '/restaurant';
  if (raw === 'restaurant-map' || raw === 'restaurantmap') return '/restaurant-map';
  if (raw === 'hoteldashboard') return '/hotel-dashboard';
  if (raw === 'hotelinfo') return '/hotel';
  if (raw === 'hotelrooms') return '/rooms';
  if (raw === 'hotelroomtypes') return '/roomtypes';
  if (raw === 'hotelpricing') return '/hotelpricing';
  if (raw === 'bookingchannel') return '/booking-channel';
  if (raw === 'roomhandover' || raw === 'room-handover') return '/room-handover';

  if (raw.includes('printers')) return '/printers';
  if (
    raw.includes('settings/integrations') ||
    raw.includes('settings/integratyions') ||
    (raw.includes('settings') && raw.includes('integrat')) ||
    raw.endsWith('/integration') ||
    raw === 'integration'
  )
    return '/integration';

  const withoutApiPrefix = raw.replace(/^\/?api\//, '');
  const noDomain = withoutApiPrefix.replace(/^[a-z]+:\/\/[^/]+/i, '');
  const cleaned = noDomain.replace(/^\/+/, '').replace(/\/+$/, '');
  if (!cleaned) return '/dashboard-restaurant';

  return normalizePath(cleaned);
};

const getDropdownSubs = (menuItem: IUserMenu) => {
  const isSettings = (menuItem.item.label || '').toLowerCase() === 'settings';

  if (isSettings) {
    return [...(menuItem.subItems || [])];
  }

  if ((menuItem.item.label || '').toLowerCase() === 'hotel') {
    return (menuItem.subItems || []).filter((sub: any) => {
      const subPath = menuUrlToUiRoute(menuRouteSource(sub));
      const subLabel = (sub.label || '').toLowerCase().replace(/[\s_-]+/g, '');
      return subPath !== '/roomtypes' && subPath !== '/hotelmanagement' && subLabel !== 'hotelmanagement';
    });
  }

  return menuItem.subItems || [];
};

const getDropdownColumnClass = (subCount: number) => {
  if (subCount > 10) return 'isThreeColumns';
  if (subCount > 5) return 'isTwoColumns';
  return 'isSingleColumn';
};

const getDropdownWidth = (subCount: number) => {
  const columnClass = getDropdownColumnClass(subCount);
  if (columnClass === 'isThreeColumns') return THREE_COLUMN_WIDTH;
  if (columnClass === 'isTwoColumns') return TWO_COLUMN_WIDTH;
  return SINGLE_COLUMN_WIDTH;
};

const TopSection: FC<Props> = ({ stage, onToggleMenu, dockPlacement, onSetDockPlacement, onNavigate }) => {
  const { i18n } = useTranslation();
  const { theme, setTheme, language, setLanguage } = useAppTheme();
  const user = useGetUser();
  const menuData = useGetMenu();
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const isIconsOnly = stage === 1;
  const isExpanded = stage === 2;
  const menuToggleLabel = isExpanded ? 'Close menu' : 'Expand menu';

  const [isThemeOpen, setIsThemeOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState<Record<number, boolean>>({});
  const [openLang, setOpenLang] = useState(false);
  const [isActionsOpen, setIsActionsOpen] = useState(false);
  const [hoveredGroupId, setHoveredGroupId] = useState<number | null>(null);
  const [dropdownPositions, setDropdownPositions] = useState<Record<number, CSSProperties>>({});
  const groupRefs = useRef<Record<number, HTMLDivElement | null>>({});

  const items = useMemo(() => {
    return (menuData ?? []).map((m) => ({
      ...m,
      item: { ...m.item },
      subItems: (m.subItems ?? []).map((s: any) => ({ ...s })),
    }));
  }, [menuData]);

  const hasActiveSub = (menuItem: IUserMenu) => {
    return (menuItem.subItems || []).some((sub: any) => location.pathname === menuUrlToUiRoute(menuRouteSource(sub)));
  };

  useEffect(() => {
    setOpenGroups((prev) => {
      const next: Record<number, boolean> = {};
      items.forEach((menuItem) => {
        const id = menuItem.item.id;
        if (!(menuItem.subItems?.length || 0)) return;
        next[id] = !!prev[id];
      });
      return next;
    });
  }, [items]);

  const labelOf = (raw: string) => {
    const value = (raw || '').trim();
    const normalized = value.toLowerCase().replace(/[\s_-]+/g, '');

    if (normalized === 'restaurantmap') return i18n.t('menu.RestaurantMap', { defaultValue: 'Restaurant Map' });
    if (normalized === 'restaurant') return i18n.t('menu.Restaurant', { defaultValue: 'Restaurant' });
    if (normalized === 'product' || normalized === 'products') return i18n.t('menu.Product', { defaultValue: 'Products' });
    if (normalized === 'dailyinventory') return i18n.t('menu.DailyInventory', { defaultValue: 'Daily Inventory' });
    if (normalized === 'hotel') return i18n.t('menu.Hotel', { defaultValue: 'Hotel' });
    if (normalized === 'hoteldashboard') {
      return i18n.t('menu.HotelDashboard', { defaultValue: i18n.t('hotelDashboard.title', { defaultValue: 'Hotel Dashboard' }) });
    }
    if (normalized === 'hotelinfo') return i18n.t('menu.HotelManagement', { defaultValue: 'Hotel Management' });
    if (normalized === 'hotelpricing') return i18n.t('menu.HotelPricing', { defaultValue: 'Hotel Pricing' });
    if (normalized === 'reservation') return i18n.t('menu.Reservation', { defaultValue: 'Reservation' });
    if (normalized === 'bookingrequest') return i18n.t('menu.BookingRequest', { defaultValue: 'Booking Request' });
    if (normalized === 'bookingchannel') return i18n.t('menu.BookingChannel', { defaultValue: 'Booking Channel' });
    if (normalized === 'area') return i18n.t('area.area', { defaultValue: 'Area' });
    if (normalized === 'umbreservations') {
      return i18n.t('umbReservations.title', { defaultValue: 'Umbrella reservations' });
    }

    return i18n.t(`menu.${raw}`, { defaultValue: raw });
  };

  const translateMenuLabel = (raw?: string, source?: string) => {
    const value = (raw || '').trim();
    const normalizedSource = (source || '').trim().toLowerCase().replace(/[\s_-]+/g, '');
    if (!value) return value;
    if (normalizedSource === 'restaurantmap') return i18n.t('menu.RestaurantMap', { defaultValue: 'Restaurant Map' });
    if (normalizedSource === 'dailyinventory') return i18n.t('menu.DailyInventory', { defaultValue: 'Daily Inventory' });
    if (normalizedSource === 'umbreservations') {
      return i18n.t('umbReservations.title', { defaultValue: 'Umbrella reservations' });
    }
    if (value.toLowerCase().replace(/[\s_-]+/g, '') === 'menudetails') {
      return i18n.t('menu.menuDetails', { defaultValue: 'Menu Details' });
    }
    return labelOf(value);
  };

  const langOptions = [
    { code: 'al', country: 'AL', label: 'Shqip' },
    { code: 'en', country: 'GB', label: 'English' },
    { code: 'it', country: 'IT', label: 'Italiano' },
  ];

  const activeCode = (language || i18n.language || 'al').split('-')[0];
  const activeLang = langOptions.find((l) => l.code === activeCode) ?? langOptions[0];
  const companyName = user?.companyName || user?.CompanyName || user?.companyName || 'Company';
  const companyLogoUrl = buildEntityFileUrl(user?.companyId, ApiConstants.EntityTypes.Company);
  const companyInitial = (companyName || 'C').trim().charAt(0).toUpperCase();
  const userDisplayName = `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim();
  const userAvatarUrl = buildEntityFileUrl(user?.id, ApiConstants.EntityTypes.User);
  const userInitial = (user?.userName || userDisplayName || 'U').trim().charAt(0).toUpperCase();

  const closeAllDropdowns = () => {
    setOpenGroups({});
    setHoveredGroupId(null);
  };

  const changeLanguage = (code: string) => {
    setLanguage(code);
    setOpenLang(false);
    setIsActionsOpen(false);
  };

  const handleLogout = () => dispatch(onLogout());
  const measureDropdownPosition = (menuItem: IUserMenu) => {
    const id = menuItem.item.id;
    const element = groupRefs.current[id];
    if (!element) return;

    const subs = getDropdownSubs(menuItem);
    const rect = element.getBoundingClientRect();
    const dropdownWidth = Math.min(getDropdownWidth(subs.length), window.innerWidth - 24);
    const left = Math.min(Math.max(12, rect.left), Math.max(12, window.innerWidth - dropdownWidth - 12));
    const top = rect.bottom - 10;

    setDropdownPositions((prev) => ({
      ...prev,
      [id]: {
        position: 'fixed',
        top,
        left,
        width: `min(${dropdownWidth}px, calc(100vw - 24px))`,
      },
    }));
  };

  const toggleGroup = (menuItem: IUserMenu) => {
    measureDropdownPosition(menuItem);
    setHoveredGroupId(null);
    setOpenGroups((prev) => (prev[menuItem.item.id] ? {} : { [menuItem.item.id]: true }));
  };
  const handleNavigate = () => onNavigate?.();

  const getIconForMenu = (label: string) => {
    switch (label.toLowerCase()) {
      case 'today': return <CottageOutlinedIcon className="tsIcon" />;
      case 'dashboard': return <DashboardIcon className="tsIcon" />;
      case 'umbrella': return <BeachAccessIcon className="tsIcon" />;
      case 'order': return <ShoppingCartIcon className="tsIcon" />;
      case 'kitchen': return <KitchenIcon className="tsIcon" />;
      case 'product': return <InventoryIcon className="tsIcon" />;
      case 'tables': return <TableChartIcon className="tsIcon" />;
      case 'invoicemanagement': return <ManageSearchIcon className="tsIcon" />;
      case 'invoices': return <ReceiptIcon className="tsIcon" />;
      case 'settings': return <SettingsSuggestIcon className="tsIcon" />;
      case 'restaurant': return <MenuBookIcon className="tsIcon" />;
      case 'printers': return <PrintIcon className="tsIcon" />;
      case 'hotel': return <HotelIcon className="tsIcon" />;
      case 'menu': return <MenuBookIcon className="tsIcon" />;
      default: return <CottageOutlinedIcon className="tsIcon" />;
    }
  };

  useEffect(() => {
    const activeIds = Object.keys(openGroups)
      .map(Number)
      .filter((id) => openGroups[id] || hoveredGroupId === id);

    if (!activeIds.length) return;

    const updatePositions = () => {
      activeIds.forEach((id) => {
        const menuItem = items.find((entry) => entry.item.id === id);
        if (menuItem) measureDropdownPosition(menuItem);
      });
    };

    updatePositions();
    window.addEventListener('resize', updatePositions);
    window.addEventListener('scroll', updatePositions, true);

    return () => {
      window.removeEventListener('resize', updatePositions);
      window.removeEventListener('scroll', updatePositions, true);
    };
  }, [hoveredGroupId, items, openGroups]);

  if (stage === 0 || dockPlacement !== 'top') return null;

  return (
    <div className={`tsRoot ${isExpanded ? 'isExpanded' : 'isDock'}`}>
      <div className="tsBar">
        <div className="tsHeader">
          <div className="tsBrandShell">
            {companyLogoUrl ? (
              <img
                src={companyLogoUrl}
                alt={companyName}
                className="tsLogo"
                onError={(e) => ((e.currentTarget as HTMLImageElement).style.display = 'none')}
              />
            ) : (
              <div className="tsLogoFallback" aria-hidden="true">{companyInitial}</div>
            )}
            {isExpanded && (
              <div className="tsBrandBlock">
                <div className="tsBrand" title={companyName}>{companyName}</div>
              </div>
            )}
          </div>
        </div>

        <div className="tsBody">
          <div className="tsNavScroller">
            <div className="tsNavViewport">
              <nav className="tsNav" aria-label="Top menu">
                {items.map((menuItem: IUserMenu) => {
                  const hasSubs = (menuItem.subItems?.length || 0) > 0;
                  const href = menuUrlToUiRoute(menuRouteSource(menuItem.item));
                  const isActive = location.pathname === href;
                  const hasActiveChild = hasActiveSub(menuItem);
                  const isOpen = !!openGroups[menuItem.item.id];
                  const isDropdownVisible = isExpanded && hasSubs && (isOpen || hoveredGroupId === menuItem.item.id);

                  return (
                  <div
                    key={menuItem.item.id}
                    className={`tsGroup ${hasSubs ? 'hasSub' : ''}`}
                    ref={(element) => {
                      groupRefs.current[menuItem.item.id] = element;
                    }}
                    onMouseEnter={() => {
                      if (isExpanded && hasSubs) {
                        measureDropdownPosition(menuItem);
                        setOpenGroups({});
                        setHoveredGroupId(menuItem.item.id);
                      }
                    }}
                    onMouseLeave={() => {
                      if (hoveredGroupId === menuItem.item.id) setHoveredGroupId(null);
                    }}
                    >
                      <div className={`tsRow ${isActive ? 'isActive' : ''} ${isActive || hasActiveChild || isDropdownVisible ? 'isGroupActive' : ''}`}>
                        <Link
                          to={href}
                          className="tsLink"
                          title={labelOf(menuItem.item.label)}
                          onClick={(e) => {
                            if (isExpanded && hasSubs) {
                              e.preventDefault();
                              toggleGroup(menuItem);
                              return;
                            }
                            closeAllDropdowns();
                            handleNavigate();
                          }}
                        >
                          <span className="tsIconWrap">{getIconForMenu(menuItem.item.label)}</span>
                          {!isIconsOnly && <span className="tsText">{labelOf(menuItem.item.label)}</span>}
                        </Link>

                        {isExpanded && hasSubs && (
                          <button
                            type="button"
                            className={`tsCollapseBtn ${isOpen ? 'isOpen' : ''}`}
                            onClick={() => toggleGroup(menuItem)}
                            aria-label="Toggle submenu"
                            title="Toggle submenu"
                          >
                            <ExpandMoreIcon fontSize="small" />
                          </button>
                        )}
                      </div>

                    {isExpanded && hasSubs && (
                      <div
                        className={`tsDropdown ${isDropdownVisible ? 'isOpen' : ''}`}
                        style={dropdownPositions[menuItem.item.id]}
                        onMouseEnter={() => {
                          if (isExpanded) {
                            measureDropdownPosition(menuItem);
                            setHoveredGroupId(menuItem.item.id);
                          }
                        }}
                        onMouseLeave={() => {
                          if (hoveredGroupId === menuItem.item.id) {
                            setHoveredGroupId(null);
                          }
                        }}
                      >
                        {(() => {
                          const subs = getDropdownSubs(menuItem);
                          const columnClass = getDropdownColumnClass(subs.length);

                          return (
                            <div className={`tsDropdownGrid ${columnClass}`}>
                              {subs.map((sub: any) => {
                                const subHref = menuUrlToUiRoute(menuRouteSource(sub));
                                const title = translateMenuLabel(sub.label, menuRouteSource(sub));
                                const isSubActive = location.pathname === subHref;

                                return (
                                  <Link
                                    key={sub.id ?? sub.url}
                                    to={subHref}
                                    className={`tsDropdownLink ${isSubActive ? 'isActive' : ''}`}
                                    title={title}
                                    onClick={() => {
                                      closeAllDropdowns();
                                      handleNavigate();
                                    }}
                                  >
                                    {title}
                                  </Link>
                                );
                              })}
                            </div>
                          );
                        })()}
                      </div>
                    )}
                    </div>
                  );
                })}
              </nav>
            </div>
          </div>

          {isExpanded && (
            <div className="tsHeaderActions">
              <NotificationIcon className="tsNotification" iconSize={20} />

              <div className="tsActionsMenu">
                <button
                  type="button"
                  className={`tsActionsToggle ${isActionsOpen ? 'isOpen' : ''}`}
                  onClick={() => setIsActionsOpen((v) => !v)}
                  aria-expanded={isActionsOpen}
                  aria-label="Open top menu actions"
                  title="Open top menu actions"
                >
                  <TuneOutlinedIcon fontSize="small" />
                </button>

                <div className={`tsActionsDropdown ${isActionsOpen ? 'isOpen' : ''}`}>
                  <div className="tsActionsSection">
                    <button
                      type="button"
                      className="tsActionButton"
                      onClick={() => {
                        onSetDockPlacement('left');
                        setIsActionsOpen(false);
                      }}
                    >
                      <WestOutlinedIcon fontSize="small" />
                      <span>Move menu left</span>
                    </button>

                    <button
                      type="button"
                      className="tsActionButton"
                      onClick={() => {
                        onToggleMenu();
                        setIsActionsOpen(false);
                      }}
                    >
                      <MenuOpenIcon fontSize="small" />
                      <span>{menuToggleLabel}</span>
                    </button>

                    <button
                      type="button"
                      className="tsActionButton"
                      onClick={() => {
                        navigate('/profile');
                        setIsActionsOpen(false);
                        handleNavigate();
                      }}
                    >
                      <AccountCircleOutlinedIcon fontSize="small" />
                      <span>{i18n.t('profile.title', { defaultValue: 'Profile' })}</span>
                    </button>
                  </div>

                  <div className="tsActionsSection">
                    <button
                      type="button"
                      className={`tsThemeMenuHeader ${isThemeOpen ? 'isOpen' : ''}`}
                      onClick={() => setIsThemeOpen((v) => !v)}
                      aria-expanded={isThemeOpen}
                    >
                      <span className="tsThemeMenuHeaderLeft">
                        <PaletteOutlinedIcon fontSize="small" />
                        <span>{i18n.t('generic.themes')}</span>
                      </span>
                      <ExpandMoreIcon fontSize="small" className="tsThemeMenuChevronIcon" />
                    </button>

                    <div className={`tsThemeMenuBody isInline ${isThemeOpen ? 'isOpen' : ''}`}>
                      <div className="tsThemeMenuList">
                        {THEMES.map((t) => (
                          <button
                            key={t}
                            type="button"
                            className={`tsThemeMenuItem ${theme === t ? 'isActive' : ''}`}
                            onClick={() => setTheme(t)}
                          >
                            <span className={`tsThemeDot dot-${t}`} />
                            <span className="tsThemeMenuText">{t}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="tsActionsSection">
                    <button type="button" className="tsLangBtn tsLangBtnFull" onClick={() => setOpenLang((v) => !v)}>
                      <ReactCountryFlag svg countryCode={activeLang.country} className="tsLangSvg" />
                      <span className="tsLangCode">{activeLang.code.toUpperCase()}</span>
                      <span className="tsLangLabel">Language</span>
                      <ExpandMoreIcon fontSize="small" className="tsLangExpandIcon" />
                    </button>

                    {openLang && (
                      <div className="tsLangMenu tsLangMenuInline">
                        {langOptions.map((opt) => (
                          <div key={opt.code} onClick={() => changeLanguage(opt.code)} className={`tsLangItem ${opt.code === activeCode ? 'isActive' : ''}`}>
                            <ReactCountryFlag svg countryCode={opt.country} className="tsLangSvg" />
                            <span className="tsLangItemLabel">{opt.label}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {user && (
                    <div className="tsActionsSection">
                      <div className="tsUser">
                        {userAvatarUrl ? <img src={userAvatarUrl} alt={user.userName || 'User'} className="tsAvatar" /> : <div className="tsAvatar tsAvatarFallback" aria-hidden="true">{userInitial}</div>}
                        <div className="tsUserMeta">
                          <div className="tsUserName" title={user.userName}>{user.userName}</div>
                          <div className="tsUserRole" title={userDisplayName}>{userDisplayName}</div>
                        </div>
                        <button className="tsLogoutBtn" onClick={handleLogout} type="button" title={i18n.t('generic.logout')}>
                          <Logout fontSize="small" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TopSection;
